import React, { useState, useEffect } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { createRecord, updateRecord } from '../../services/recordStorageService';
import { persistedClientRecords } from '../../services/mockData/clients';
import { getRecordsForObject } from '../../services/recordStorageService';
import { shouldUseGobodo } from '../../utils/gobodoUtils';
import { TabbedModal } from './TabbedModal';
import { usePageLayout } from '../../hooks/useObjectManager';
import { EditSection } from '../edit/EditSection';
import { SearchableSelect } from './SearchableSelect';
import { useToast } from '../../contexts/ToastContext';
import { ListViewRecord } from '../../types/listView';
import { User, CheckSquare, Package, Mail, FileText, Paperclip, History, Link2 } from 'lucide-react';
import { validateRecord, type ValidationError } from '../../services/queries/validationRuleQueries';
import { ValidationErrors } from '../common/ValidationErrors';
import { RelatedRecordsTab } from '../tabs/RelatedRecordsTab';
import { ActivityTab } from '../tabs/ActivityTab';

interface ContactModalProps {
  isOpen: boolean;
  onClose: () => void;
  contact?: ListViewRecord | null;
}

export const ContactModal: React.FC<ContactModalProps> = ({
  isOpen,
  onClose,
  contact
}) => {
  const { success, error: showError } = useToast();
  const queryClient = useQueryClient();
  const isEditMode = !!contact;

  // Get page layout for this contact
  const { data: pageLayout, isLoading: loadingLayout } = usePageLayout('Contact__c', contact);

  console.log('📝 Contact Modal - Page Layout:', {
    contactId: contact?.id,
    layoutName: pageLayout?.name,
    layoutApplication: pageLayout?.applicationName,
    sectionCount: pageLayout?.sections?.length,
    sections: pageLayout?.sections?.map(s => s.title)
  });

  const [formData, setFormData] = useState<Record<string, any>>({
    FirstName__c: '',
    LastName__c: '',
    Title__c: '',
    Department__c: '',
    Email__c: '',
    Phone__c: '',
    MobilePhone__c: '',
    AccountId__c: '',
    AccountRelField__c: '',
    IsPrimary__c: false,
    Owner__c: 'John Smith'
  });

  const [notes, setNotes] = useState<string[]>([]);
  const [newNote, setNewNote] = useState('');
  const [errors, setErrors] = useState<Record<string, string>>({});

  const shouldGobodo = shouldUseGobodo();
  const { data: gobodoClients } = useQuery({
    queryKey: ['modalClients'],
    queryFn: () => getRecordsForObject('Client__c'),
    enabled: shouldGobodo && isOpen
  });
  const { data: gobodoAccounts } = useQuery({
    queryKey: ['modalAccounts'],
    queryFn: () => getRecordsForObject('Account'),
    enabled: shouldGobodo && isOpen
  });
  const allClients = (shouldGobodo && gobodoClients) ? gobodoClients : persistedClientRecords.get();
  const allAccounts = shouldGobodo && gobodoAccounts ? gobodoAccounts : [];
  const clientOptions = (shouldGobodo ? allAccounts : allClients).map((c: any) => ({
    value: c.id,
    label: (c.name ?? c.label ?? (c as any).Name__c) as string
  }));

  // Populate form in edit mode
  useEffect(() => {
    const accountVal = (contact?.AccountRelField__c ?? contact?.accountId ?? contact?.AccountId__c) as string || '';
    if (contact) {
      setFormData({
        FirstName__c: (contact.FirstName__c || contact.firstName) as string || '',
        LastName__c: (contact.LastName__c || contact.lastName) as string || '',
        Title__c: (contact.Title__c || contact.title) as string || '',
        Department__c: (contact.Department__c || contact.department) as string || '',
        Email__c: (contact.Email__c || contact.email) as string || '',
        Phone__c: (contact.Phone__c || contact.phone) as string || '',
        MobilePhone__c: (contact.MobilePhone__c || contact.mobilePhone) as string || '',
        AccountId__c: accountVal,
        AccountRelField__c: accountVal,
        IsPrimary__c: (contact.IsPrimary__c || contact.isPrimary) as boolean || false,
        Owner__c: (contact.Owner__c || contact.owner) as string || 'John Smith'
      });
      setNotes(contact.notes as string[] || []);
    } else {
      // Reset form for create mode
      setFormData({
        FirstName__c: '',
        LastName__c: '',
        Title__c: '',
        Department__c: '',
        Email__c: '',
        Phone__c: '',
        MobilePhone__c: '',
        AccountId__c: '',
        AccountRelField__c: '',
        IsPrimary__c: false,
        Owner__c: 'John Smith'
      });
      setNotes([]);
    }
    setErrors({});
    setNewNote('');
  }, [contact, isOpen]);

  // Handle field changes (for dynamic fields)
  const handleFieldChange = (fieldName: string, value: any) => {
    setFormData(prev => ({ ...prev, [fieldName]: value }));

    // Clear error when user changes field
    if (errors[fieldName]) {
      setErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[fieldName];
        return newErrors;
      });
    }
  };

  // Handle change events (legacy support)
  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value, type } = e.target;

    if (type === 'checkbox') {
      const checked = (e.target as HTMLInputElement).checked;
      handleFieldChange(name, checked);
    } else {
      handleFieldChange(name, value);
    }
  };

  const handleAddNote = () => {
    if (newNote.trim()) {
      setNotes([...notes, newNote.trim()]);
      setNewNote('');
    }
  };

  const handleRemoveNote = (index: number) => {
    setNotes(notes.filter((_, i) => i !== index));
  };

  const validate = () => {
    const newErrors: Record<string, string> = {};

    const firstName = formData.FirstName__c || '';
    const lastName = formData.LastName__c || '';
    const accountId = (shouldGobodo ? formData.AccountRelField__c : formData.AccountId__c) ?? formData.AccountId__c ?? '';
    const email = formData.Email__c || '';

    if (!firstName.trim()) {
      newErrors.FirstName__c = 'First name is required';
    }

    if (!lastName.trim()) {
      newErrors.LastName__c = 'Last name is required';
    }

    if (!accountId) {
      const errKey = shouldGobodo ? 'AccountRelField__c' : 'AccountId__c';
      newErrors[errKey] = shouldGobodo ? 'Account is required' : 'Client is required';
    }

    if (email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      newErrors.Email__c = 'Invalid email format';
    }

    console.log('[ContactModal] Validation:', { firstName, lastName, accountId, email, errors: newErrors });
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const saveMutation = useMutation({
    mutationFn: async (data: Record<string, any> & { notes: string[] }) => {
      const accountVal = data.AccountRelField__c ?? data.AccountId__c ?? data.accountId;
      const gobodoData: Record<string, any> = {
        FirstName__c: data.FirstName__c,
        LastName__c: data.LastName__c,
        Title__c: data.Title__c,
        Department__c: data.Department__c,
        Email__c: data.Email__c,
        Phone__c: data.Phone__c,
        MobilePhone__c: data.MobilePhone__c,
        IsPrimary__c: data.IsPrimary__c,
        Owner__c: data.Owner__c,
        LastContact__c: new Date().toISOString()
      };
      if (shouldGobodo) {
        gobodoData.AccountRelField__c = accountVal || undefined;
      } else {
        gobodoData.AccountId__c = accountVal || undefined;
      }

      // Remove null/empty values
      Object.keys(gobodoData).forEach(key => {
        if (gobodoData[key] === null || gobodoData[key] === '' || gobodoData[key] === undefined) {
          delete gobodoData[key];
        }
      });

      if (isEditMode && contact) {
        const updated = await updateRecord('Contact__c', String(contact.id), gobodoData);
        if (!updated) throw new Error('Failed to update contact');
        return { ...contact, ...data, id: updated.id };
      } else {
        const created = await createRecord('Contact__c', gobodoData);
        return { ...data, id: created.id };
      }
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['contactListViews'] });
      queryClient.invalidateQueries({ queryKey: ['contactRecords'] });
      queryClient.invalidateQueries({ queryKey: ['gobodoRecords', 'Contact__c'] });
      queryClient.invalidateQueries({ queryKey: ['gobodoRecords', 'Contact'] });
      if (isEditMode) {
        queryClient.invalidateQueries({ queryKey: ['contact', contact?.id] });
      }

      const fullName = `${(data as any).FirstName__c || ''} ${(data as any).LastName__c || ''}`.trim() || 'Contact';
      success(
        isEditMode ? 'Contact updated' : 'Contact created',
        isEditMode ? `${fullName} has been updated` : `${fullName} has been created`
      );
      onClose();
    },
    onError: (error) => {
      console.error('[ContactModal] Save error:', error);
      showError('Error', 'Failed to save contact. Please try again.');
    }
  });

  const handleSubmit = (e?: React.FormEvent | React.MouseEvent) => {
    e?.preventDefault();
    console.log('[ContactModal] handleSubmit called with formData:', formData);

    if (!validate()) {
      console.log('[ContactModal] Validation failed');
      showError('Validation Error', 'Please fix the errors before submitting');
      return;
    }
    console.log('[ContactModal] Validation passed, proceeding with save');

    console.log('[ContactModal] Calling saveMutation.mutate with:', { ...formData, notes });
    saveMutation.mutate({ ...formData, notes });
  };

  // Define tabs based on mode
  const tabs = isEditMode
    ? [
        // EDIT MODE: All tabs
        { id: 'overview', label: 'Overview', icon: User },
        { id: 'related', label: 'Related', icon: Link2 },
        { id: 'tasks', label: 'Tasks', icon: CheckSquare },
        { id: 'products', label: 'Products', icon: Package },
        { id: 'emails', label: 'Emails', icon: Mail },
        { id: 'notes', label: 'Notes', icon: FileText },
        { id: 'files', label: 'Files', icon: Paperclip },
        { id: 'history', label: 'History', icon: History }
      ]
    : [
        // CREATE MODE: Overview only
        { id: 'overview', label: 'Overview', icon: User }
      ];

  return (
    <TabbedModal
      isOpen={isOpen}
      onClose={onClose}
      title={isEditMode ? 'Edit Contact' : 'New Contact'}
      tabs={tabs}
      maxWidth="2xl"
      actions={
        <div className="flex items-center justify-end gap-3">
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2 text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-[#334155] rounded-lg transition-colors"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={handleSubmit}
            disabled={saveMutation.isPending}
            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {saveMutation.isPending
              ? 'Saving...'
              : isEditMode ? 'Save Changes' : 'Create Contact'
            }
          </button>
        </div>
      }
    >
      {(activeTab) => (
        <form onSubmit={handleSubmit} className="space-y-6">
          {activeTab === 'overview' && (
            <div className="max-h-[60vh] overflow-y-auto pr-2">
              {loadingLayout ? (
                // Loading skeleton
                <div className="space-y-6">
                  <div className="animate-pulse space-y-4">
                    {[1, 2, 3].map(i => (
                      <div key={i}>
                        <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded w-1/4 mb-2"></div>
                        <div className="h-10 bg-gray-200 dark:bg-gray-700 rounded"></div>
                      </div>
                    ))}
                  </div>
                </div>
              ) : pageLayout?.sections && pageLayout.sections.length > 0 ? (
                // Dynamic sections from Object Manager
                pageLayout.sections.map(section => (
                  <EditSection
                    key={section.id}
                    section={section}
                    objectApiName="Contact__c"
                    formData={formData}
                    onChange={handleFieldChange}
                    errors={errors}
                    disabled={saveMutation.isPending}
                  />
                ))
              ) : (
                // No sections configured
                <div className="text-center py-8 text-gray-500 dark:text-gray-400">
                  <p>No page layout configured for Contact.</p>
                  <p className="text-sm mt-2">
                    Go to Object Manager to configure page layouts.
                  </p>
                </div>
              )}
            </div>
          )}

          {activeTab === 'related' && contact?.id && (
            <RelatedRecordsTab
              recordId={contact.id}
              objectType="Contact__c"
              recordName={`${contact.firstName || ''} ${contact.lastName || ''}`}
            />
          )}

          {activeTab === 'tasks' && (
            <div className="text-center py-12">
              <CheckSquare className="w-12 h-12 text-gray-400 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2">
                No Tasks
              </h3>
              <p className="text-sm text-gray-600 dark:text-gray-400">
                Tasks related to this contact will appear here.
              </p>
            </div>
          )}

          {activeTab === 'products' && (
            <div className="text-center py-12">
              <Package className="w-12 h-12 text-gray-400 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2">
                No Products
              </h3>
              <p className="text-sm text-gray-600 dark:text-gray-400">
                Products associated with this contact will appear here.
              </p>
            </div>
          )}

          {activeTab === 'emails' && (
            <div className="text-center py-12">
              <Mail className="w-12 h-12 text-gray-400 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2">
                No Emails
              </h3>
              <p className="text-sm text-gray-600 dark:text-gray-400">
                Email correspondence with this contact will appear here.
              </p>
            </div>
          )}

          {activeTab === 'notes' && (
            <div className="space-y-4">
              <div className="space-y-2">
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                  Add Note
                </label>
                <textarea
                  value={newNote}
                  onChange={(e) => setNewNote(e.target.value)}
                  placeholder="Type your note here..."
                  rows={4}
                  className="w-full px-3 py-2 bg-white dark:bg-[#0f172a] border border-gray-300 dark:border-gray-600 rounded-lg text-gray-900 dark:text-white placeholder-gray-400 dark:placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
                <button
                  type="button"
                  onClick={handleAddNote}
                  className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                >
                  Add Note
                </button>
              </div>

              {notes.length > 0 ? (
                <div className="space-y-2">
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                    Notes ({notes.length})
                  </label>
                  <div className="space-y-2">
                    {notes.map((note, index) => (
                      <div
                        key={index}
                        className="p-3 bg-gray-50 dark:bg-[#0f172a] border border-gray-200 dark:border-gray-700 rounded-lg"
                      >
                        <p className="text-sm text-gray-900 dark:text-white whitespace-pre-wrap">{note}</p>
                        <button
                          type="button"
                          onClick={() => handleRemoveNote(index)}
                          className="text-sm text-red-600 dark:text-red-400 hover:underline mt-2"
                        >
                          Remove
                        </button>
                      </div>
                    ))}
                  </div>
                </div>
              ) : (
                <div className="text-center py-8">
                  <FileText className="w-12 h-12 text-gray-400 mx-auto mb-4" />
                  <p className="text-sm text-gray-600 dark:text-gray-400">
                    No notes yet. Add your first note above.
                  </p>
                </div>
              )}
            </div>
          )}

          {activeTab === 'files' && (
            <div className="text-center py-12">
              <Paperclip className="w-12 h-12 text-gray-400 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2">
                No Files
              </h3>
              <p className="text-sm text-gray-600 dark:text-gray-400">
                Documents and files attached to this contact will appear here.
              </p>
            </div>
          )}

          {activeTab === 'history' && contact?.id && (
            <ActivityTab
              recordId={contact.id}
              objectType="Contact__c"
              recordName={`${contact.firstName || ''} ${contact.lastName || ''}`}
            />
          )}
        </form>
      )}
    </TabbedModal>
  );
};
